import React, { useState, useRef, useEffect } from "react";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import {
  FileUpload,
  FileUploadSelectEvent,
} from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import ComplaintService from "../../services/complaint/ComplaintService";
import {
  CreateComplaintRequestDTO,
} from "../../models/complaints/complaint-model";
import "./ProductComplaintDialog.css";

interface ProductOrderData {
  orderID: number;
  orderDetailID: number;
  orderInvoiceID: number;
  productID: number;
  productName: string;
  invoiceNo: string;
  orderDate: string;
  invoicedDate?: string;
  categoryName?: string;
  subCategoryName?: string;
  totalQuantity?: number;
  totalGrossAmount?: number;
}

enum ComplaintTypeEnum {
  Internal = 1,
  Product = 2,
  Service = 3,
}


interface ProductComplaintDialogProps {
  visible: boolean;
  onHide: () => void;
  productData: ProductOrderData | null;
  onSuccess?: () => void;
}

const ProductComplaintDialog: React.FC<ProductComplaintDialogProps> = ({
  visible,
  onHide,
  productData,
  onSuccess,
}) => {
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<any>(null);

  // State management - Form fields
  const [description, setDescription] = useState<string>("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginUser, setLoginUser] = useState<any>(null);

  // Effects
  useEffect(() => {
    const loginData = localStorage.getItem("user");
    if (loginData) {
      setLoginUser(JSON.parse(loginData));
    }
  }, []);

  // Clear individual field error
  const clearFieldError = (fieldName: string) => {
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!description || description.length < 10) {
      newErrors.description =
        "Description is required and must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle file selection
  const handleFileSelect = (e: any) => {
    const files = Array.from(e.files || []) as File[];
    setAttachments(files);
  };

  // Handle file removal
  const handleFileRemove = (e: any) => {
    const remainingFiles = attachments.filter(
      (file) => file.name !== e.file.name || file.size !== e.file.size
    );
    setAttachments(remainingFiles);
  };

  // Handle upload clear
  const handleUploadClear = () => {
    setAttachments([]);
  };

  // Handle submit
  const handleSubmitComplaint = async () => {
    if (!validateForm()) {
      showMessage("Error", "Please fill all required fields", "error");
      return;
    }

    if (!productData) {
      showMessage("Error", "Product data is missing", "error");
      return;
    }

    try {
      setIsLoading(true);

      const payload: CreateComplaintRequestDTO = {
        complaintTypeId: ComplaintTypeEnum.Product, // Product complaint type
        description: description,
        priority: "Medium", // Default priority for product complaints
        customerId: loginUser?.userID || 0,
        invoiceId: productData.orderInvoiceID,
        orderDetailId: productData.orderDetailID,
      };

      // Create form data for file upload
      const complaintData = {
        ...payload,
        attachments: attachments,
      };

      const response = await ComplaintService.createComplaintV2(complaintData);

      if (response.isSuccess) {
        showMessage(
          "Success",
          "Complaint registered successfully",
          "success"
        );
        resetForm();
        onHide();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        showMessage(
          "Error",
          response.message || "Failed to create complaint",
          "error"
        );
      }
    } catch (error: any) {
      console.error("Error submitting complaint:", error);
      showMessage(
        "Error",
        error?.message || "An error occurred while submitting the complaint",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setDescription("");
    setAttachments([]);
    setErrors({});
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
  };

  // Show toast message
  const showMessage = (severity: string, summary: string, type: any) => {
    toast.current?.show({
      severity: type,
      summary: severity,
      detail: summary,
      life: 3000,
    });
  };

  const handleDialogHide = () => {
    resetForm();
    onHide();
  };

  const footer = (
    <div className="complaint-dialog-footer">
      <Button
        label="Reset"
        icon="pi pi-times"
        onClick={resetForm}
        className="p-button-text"
        disabled={isLoading}
      />
      <Button
        label="Submit Complaint"
        icon="pi pi-check"
        onClick={handleSubmitComplaint}
        loading={isLoading}
        disabled={isLoading}
        className="p-button-success"
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        onHide={handleDialogHide}
        header="Register Product Complaint"
        modal
        className="product-complaint-dialog"
        style={{ width: "50vw" }}
        footer={footer}
      >
        {isLoading ? (
          <div className="loading-container">
            <ProgressSpinner />
            <p>Processing...</p>
          </div>
        ) : (
          <div className="complaint-form">
            {/* Invoice Number - Read Only */}
            <div className="form-field">
              <label htmlFor="invoiceNo">#Invoice No</label>
              <InputText
                id="invoiceNo"
                value={productData?.invoiceNo || ""}
                disabled
                className="w-full"
              />
            </div>

            {/* Product Name - Read Only */}
            <div className="form-field">
              <label htmlFor="productName">Product Name</label>
              <InputText
                id="productName"
                value={productData?.productName || ""}
                disabled
                className="w-full"
              />
            </div>

            {/* Description */}
            <div className="form-field">
              <label htmlFor="description" className="required-label">
                Description{" "}
                <span style={{ color: "red", fontSize: "18px" }}>*</span>
              </label>
              <InputTextarea
                id="description"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  clearFieldError("description");
                }}
                onBlur={() => clearFieldError("description")}
                rows={6}
                className={`w-full ${errors.description ? "p-invalid" : ""}`}
                placeholder="Enter complaint description (minimum 10 characters)"
              />
              {errors.description && (
                <small className="p-error">{errors.description}</small>
              )}
            </div>

            {/* File upload */}
            <div className="form-field">
              <label htmlFor="attachments">
                Attachment (Documents/Images)
              </label>
              <FileUpload
                ref={fileUploadRef}
                name="attachments"
                accept="image/*,.pdf,.doc,.docx"
                maxFileSize={5000000}
                multiple
                auto={false}
                customUpload
                onSelect={handleFileSelect}
                onRemove={handleFileRemove}
                onClear={handleUploadClear}
                emptyTemplate={
                  <p className="m-0">Drag and drop files here to upload.</p>
                }
                chooseOptions={{
                  icon: "pi pi-fw pi-plus",
                  label: "Choose",
                  className: "p-button-success",
                }}
                uploadOptions={{
                  className: "hidden-button",
                }}
                cancelOptions={{
                  className: "hidden-button",
                }}
              />
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default ProductComplaintDialog;
